import React from 'react';
import type { EventDashboardCountRow } from '../../data/events';

interface EventLeaderboardModalProps {
  rows: EventDashboardCountRow[];
  onClose: () => void;
}

function fmtCurrency(value: number): string {
  return value.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export default function EventLeaderboardModal({ rows, onClose }: EventLeaderboardModalProps) {
  const grandTotal = rows.reduce((s, r) => s + (Number(r.total) || 0), 0);
  const grandUpcoming = rows.reduce((s, r) => s + (Number(r.upcoming) || 0), 0);
  const grandInProgress = rows.reduce((s, r) => s + (Number(r.in_progress) || 0), 0);
  const grandCompleted = rows.reduce((s, r) => s + (Number(r.completed) || 0), 0);
  const grandCompletedAmt = rows.reduce((s, r) => s + (Number(r.completed_amount) || 0), 0);

  const cellPad = '0.5rem 0.75rem';
  const headPad = '0.625rem 0.75rem';

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className="settings-card"
        style={{
          width: '100%',
          maxWidth: 780,
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          margin: 'auto',
          borderRadius: 'var(--radius-lg, 12px)',
          boxShadow: 'var(--shadow-lg, 0 8px 32px rgba(0,0,0,.18))',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Event Leaderboard"
      >
        <div className="settings-card__header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, position: 'relative' }}>
          <h2 className="settings-card__title m0">Event Leaderboard</h2>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', padding: '0.25rem', lineHeight: 1, minWidth: 0, fontSize: '1.1rem' }}
            onClick={onClose}
            aria-label="Close leaderboard"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="settings-card__body" style={{ overflow: 'auto', flexGrow: 1, padding: 0 }}>
          {rows.length === 0 ? (
            <p className="fg-muted" style={{ padding: '1.5rem', textAlign: 'center' }}>No event data available.</p>
          ) : (
            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '0.625rem 1rem' }}>#</th>
                  <th style={{ textAlign: 'left', padding: headPad }}>Location</th>
                  <th style={{ textAlign: 'right', padding: headPad }}>Total</th>
                  <th style={{ textAlign: 'right', padding: headPad }}>Upcoming</th>
                  <th style={{ textAlign: 'right', padding: headPad }}>In Progress</th>
                  <th style={{ textAlign: 'right', padding: headPad }}>Completed</th>
                  <th style={{ textAlign: 'right', padding: headPad }}>Completed $</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.site_location_id}>
                    <td style={{ padding: '0.5rem 1rem', fontWeight: 600 }} className="fg-muted text-xs">{idx + 1}</td>
                    <td style={{ padding: cellPad }}>{row.location_name}</td>
                    <td style={{ textAlign: 'right', padding: cellPad, fontWeight: 600 }}>{Number(row.total).toLocaleString()}</td>
                    <td style={{ textAlign: 'right', padding: cellPad }}>{Number(row.upcoming).toLocaleString()}</td>
                    <td style={{ textAlign: 'right', padding: cellPad }}>{Number(row.in_progress).toLocaleString()}</td>
                    <td style={{ textAlign: 'right', padding: cellPad }}>{Number(row.completed).toLocaleString()}</td>
                    <td style={{ textAlign: 'right', padding: cellPad }} className="fg-muted">{fmtCurrency(Number(row.completed_amount) || 0)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border-default, #ccc)' }}>
                  <td style={{ padding: '0.625rem 1rem' }} />
                  <td style={{ padding: headPad, fontWeight: 700 }}>All Locations</td>
                  <td style={{ textAlign: 'right', padding: headPad, fontWeight: 700 }}>{grandTotal.toLocaleString()}</td>
                  <td style={{ textAlign: 'right', padding: headPad, fontWeight: 700 }}>{grandUpcoming.toLocaleString()}</td>
                  <td style={{ textAlign: 'right', padding: headPad, fontWeight: 700 }}>{grandInProgress.toLocaleString()}</td>
                  <td style={{ textAlign: 'right', padding: headPad, fontWeight: 700 }}>{grandCompleted.toLocaleString()}</td>
                  <td style={{ textAlign: 'right', padding: headPad, fontWeight: 700 }}>{fmtCurrency(grandCompletedAmt)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
